import { permissionsByEntity, useCheckPermissions } from '@cmg/auth';
import { UUID } from '@cmg/common';
import React from 'react';

import { useGetOfferingEntitlementsQuery } from '../../../../../common/util/check-entitlements/useGetOfferingEntitlementsQuery';
import routeFactory from '../../../../../common/util/routeFactory';
import { ReactRouterDomainNavTabProps } from '../../../../../design-system/react-router/ReactRouterDomainNavTab';
import { OfferingStage, OfferingStatus } from '../../../../../graphql';

type Props = Readonly<{
  offeringId: UUID | undefined;
  offeringStatus: OfferingStatus | undefined;
  offeringStage: OfferingStage | undefined;
}>;

export const useTradeReleaseRoute = ({
  offeringId,
  offeringStatus,
  offeringStage,
}: Props): ReactRouterDomainNavTabProps | undefined => {
  const hasRead = useCheckPermissions([permissionsByEntity.Trade.READ]);
  const hasFull = useCheckPermissions([permissionsByEntity.Trade.FULL]);

  const isPriced = offeringStatus === OfferingStatus.Priced;

  const { data: entitlements } = useGetOfferingEntitlementsQuery({ offeringId });
  const { canRead } = React.useMemo(
    () => ({
      canRead: hasRead && entitlements.includes('READ:INSTITUTIONAL_ALLOCATION_FINAL'),
      canUpdate: hasFull && entitlements.includes('FULL:INSTITUTIONAL_ALLOCATION_FINAL'),
    }),
    [hasRead, hasFull, entitlements]
  );

  if (!offeringId) {
    return undefined;
  }

  if (!isPriced) {
    return undefined;
  }

  if (!canRead) {
    return undefined;
  }

  if (offeringStage !== OfferingStage.Published) {
    return undefined;
  }

  const href = routeFactory.tradeRelease.getUrlPath({ offeringId });
  return {
    href,
    label: 'Trade Release',
  };
};
